import configuration from './configuration'
import utils from './WorldUtils'
import $ from 'jquery'
import {TweenLite} from 'gsap'
let THREE = window.THREE

const SceneController = function (chapterIndex, data, $bus) {
  this.init(chapterIndex, data, $bus)
}

SceneController.prototype = {
  init (chapterIndex, data, $bus) {
    this.$bus = $bus
    this.chapterIndex = chapterIndex
    this.data = data
    this.arrows = {}
    this.backgrounds = {}
    this.cursors = {}
    this.intros = {}
    this.pointers = {}
    this.titles = {}
    this.zones = {}
    this.toPositions = {}
    this.outlines = {}
    this.positionFactor = -20
  },
  addToAndfadeIn (scene) {
    this.addTo(scene)
    return this.fadeScene(0, 1)
  },
  addTo (scene) {
    this.stopFade()
    scene.add(this.scene)
  },
  removeScene () {
    this.scene.parent.remove(this.scene)
  },
  fadeOutScene () {
    this.hideOutline()
    return this.fadeScene(1, 0).then((objects) => {
      this.removeScene()
      objects.forEach(object => { object.material.opacity = 1 })
    })
  },
  stopFade () {
    if (this.fadeAnimation) this.fadeAnimation.pause()
  },
  fadeScene (from, to) {
    const objects = []
    this.scene.traverse(object => {
      if (object.material) {
        object.material.transparent = true
        objects.push(object)
      }
    })
    return new Promise((resolve, reject) => {
      const tweenObject = {val: from}
      const vars = {
        val: to,
        onUpdate: (o) => {
          objects.forEach(object => { object.material.opacity = o.val })
        },
        onUpdateParams: [tweenObject],
        onComplete: resolve.bind(this, objects)
      }
      this.fadeAnimation = TweenLite.to(tweenObject, 1, vars)
    })
  },
  getZIndex () {
    return 0
  },
  disable () {
    this.removeEventListeners()
  },
  enable () {
    this.addEventListeners()
    this.$bus.$emit('chapter-enabled', this)
  },
  removeEventListeners () {
    this.$bus.$off('mouse-move', this.mouseMoveListener)
    this.$bus.$off('click', this.clickListener)
    this.$bus.$off('touchend', this.touchendListener)
    this.$bus.$off('touchmove', this.touchmoveListener)
  },
  addEventListeners () {
    this.mouseMoveListener = this.onMouseMove.bind(this)
    this.clickListener = this.onClick.bind(this)
    this.touchendListener = this.onTouchend.bind(this)
    this.touchmoveListener = this.onTouchmove.bind(this)
    this.$bus.$on('mouse-move', this.mouseMoveListener)
    this.$bus.$on('touchend', this.touchendListener)
    this.$bus.$on('touchmove', this.touchmoveListener)
    this.$bus.$on('click', this.clickListener)
    this.$bus.$on('USER_IS_TOUCHING', this.onUserTouch.bind(this))
  },
  onUserTouch () {
    this.userIsTouching = true
  },
  onTouchend (e) {
    if (!this.pinched) {
      this.onClick(e)
    }
    if (!e.touchEvent.touches.length) {
      this.pinched = false
    }
  },
  onTouchmove (e) {
    if (e.touches.length === 2) {
      this.pinched = true
    }
  },
  onClick (e) {
    let hoveredZone = this.getHoveredZone(e)
    if (hoveredZone) {
      if (this.userIsTouching) {
        this.$bus.$emit('zone-touched', hoveredZone, e.moved)
      } else {
        this.$bus.$emit('zone-clicked', hoveredZone, e.moved)
      }
    } else if (e.moved) {
      // if no zone, snap to previous zone
    }
  },
  onMouseMove (e) {
    this.showOutlineForHoveredZone(e)
  },
  showOutlineForHoveredZone (e) {
    if (this.getHoveredZone(e)) {
      let hoveredZone = this.getHoveredZone(e).object.zoneIndex
      if (this.hoveredZone !== hoveredZone) {
        let outline = this.zones[hoveredZone].outline
        if (this.outline) {
          this.outline.material.visible = false
        }
        if (outline) {
          outline.material.visible = true
        }
        this.outline = outline
        this.hoveredZone = hoveredZone
        this.$bus.$emit('zone-hovered', hoveredZone)
      }
    } else {
      if (this.hoveredZone) {
        let outline = this.zones[this.hoveredZone].outline
        if (outline) {
          outline.material.visible = false
        }
        this.outline = null
        this.hoveredZone = null
        this.$bus.$emit('no-zone-hovered')
      }
    }
  },
  hideOutline () {
    if (this.hoveredZone) {
      let outline = this.zones[this.hoveredZone].outline
      if (outline) {
        outline.material.visible = false
      }
    }
  },
  getHoveredZone (e, backwards = false) {
    let intersects = e.raycaster
      .intersectObjects(this.getClickableObjects(), true)
      .filter(intersect => {
        return intersect.object.zoneIndex !== this.currentZoneIndex
      })
    if (intersects.length) {
      return intersects[0]
    }
    return null
  },
  getClickableObjects () {
    return Object.keys(this.zones)
      .map(key => {
        return this.zones[key].mesh
      })
  },
  getFilename (param) {
    return 'chapter-' + param
  },
  reload () {
    const parentObject = this.scene.parent
    parentObject.remove(this.scene)
    return this.load(this.customProperties, this.loadingManager)
      .then(() => {
        parentObject.add(this.scene)
      })
  },
  load (customProperties, loadingManager) {
    this.customProperties = customProperties
    this.loadingManager = loadingManager
    return this.loadMaterials(this.chapterIndex, loadingManager)
    .then(materials => {
      return this.loadObjects(customProperties, materials, this.chapterIndex, loadingManager)
    })
    .then(object => {
      this.scene = object
      object.name = this.chapterIndex
      return this
    })
  },
  loadMaterials (chapterIndex, loadingManager) {
    return new Promise((resolve, reject) => {
      let mtlLoader = new THREE.MTLLoader(loadingManager)
      mtlLoader.setPath('./static/data/')
      const smallScreen = window.screen.width <= 1024
      // const smallScreen = false
      const suffix = smallScreen ? '-sm.mtl' : '.mtl'
      mtlLoader.load(this.getFilename(chapterIndex) + suffix, materials => {
        materials.preload()
        resolve(materials)
      }, undefined, resolve)
    })
  },
  traverseObject (object, customProperties) {
    utils.traverseObject(object, customProperties, this)
  },
  loadObjects (customProperties, materials, chapterIndex, loadingManager) {
    return new Promise((resolve, reject) => {
      let loader = new THREE.OBJLoader(loadingManager)
      loader.setMaterials(materials)
      loader.setPath('./static/data/')
      loader.load(this.getFilename(chapterIndex) + '.obj', object => {
        object.scale.set(100, 100, 100)
        this.traverseObject(object, customProperties)

        // map toPositions and outlines to zones
        Object.keys(this.zones).forEach((key) => {
          let zone = this.zones[key]
          zone.toPosition = this.toPositions[key]
          zone.outline = this.outlines[key]
        })

        // map backgrounds to zones
        Object.keys(configuration.zoneMapping.background).forEach(key => {
          configuration.zoneMapping.background[key].forEach(zoneIndex => {
            let zone = this.zones[zoneIndex]
            if (zone) {
              zone.background = this.backgrounds[key]
            }
          })
        })
        resolve(object)
      }, undefined, resolve)
    })
  },
  hideTexts (cssScene) {
    window.clearTimeout(this.showTask)
    $('.block').removeClass('shown')
    while (cssScene.children.length > 0) {
      cssScene.remove(cssScene.children[0])
    }
    $('.overlay').empty()
    return new Promise((resolve, reject) => {
      window.setTimeout(resolve, 1000)
    })
  },
  showTexts () {
    this.showTask = window.setTimeout(() => {
      $('.block').addClass('shown open')
    }, 500)
  },
  showTextsForZone (textIndex, cssScene) {
    this.hideTexts(cssScene)
      .then(() => {
        this.replaceOverlayTexts(textIndex, cssScene)
        this.showTexts()
      })
  },
  blockImage (text) {
    if (!text.block.image) return ''
    return `<img src="${text.block.image}">`
  },
  replaceOverlayTexts (textIndex, cssScene) {
    let $overlay = $('.overlay')
    let text = this.data.subChapters[textIndex]
    this.cssElements = {
      intro: document.createElement('div'),
      block: $('<div class="popup block-container">')[0],
      explainers: {},
      pointers: []
    }
    this.text3DObjects = {}
    if (text.block) {
      if (text.block.explainers) {
        text.block.explainers.forEach(explainer => {
          let explainerElement = this.cssElements.explainers[explainer.keyword] =
          $('<div class="explainer block">')[0]
          explainerElement.innerHTML = `
          <div class="close">x</div>
          <h4>${explainer.keyword}</h4>
          <p>${explainer.text}</p>
          `
        })
      }
      let blockText = text.block.text
      if (blockText.indexOf('{') !== -1 && blockText.indexOf('}') !== -1) {
        blockText.match(/\{(.*?)\}/g).forEach((match, index) => {
          if (match === '{image}') {
            if (text.block.image) {
              let img = '<img class="block-image" src="' + text.block.image + '"/>'
              blockText = blockText.replace(match, img)
            }
          } else {
            let keyword = match.substring(1, match.length - 1)
            let span = '<span class="anchor" data-keyword="' + keyword + '">' + keyword + '</span>'
            blockText = blockText.replace(match, span)
          }
        })
      }

      this.cssElements.block.innerHTML = `
        <div class="block">
          <h3>${text.title}</h3>
          <div class="block-content">
            <p>${blockText}</p>
            ${this.blockImage(text)}
          </div>
        </div>`

      $(this.cssElements.block).find('.block').click(function (e) {
        if (!$(e.target).hasClass('anchor')) {
          $(this).toggleClass('open')
        }
      })

      $overlay.append(this.cssElements.block)
      $overlay.find('.anchor').each((index, element) => {
        let $element = $(element)
        let keyword = $element.data('keyword')
        let explainerElement = this.cssElements.explainers[keyword]
        $element.click(() => {
          $overlay.find('.explainer').remove()
          this.cssElements.block.append(explainerElement)
          $(explainerElement).find('.close').click(() => {
            $(explainerElement).remove()
            this.openElement = null
            this.$bus.$emit('hide-cursors', false)
          })
          if (this.openElement === keyword) {
            $(explainerElement).remove()
            this.openElement = null
          } else {
            this.openElement = keyword
          }
        })
      })
    }

    if (text.intro && this.intros[textIndex]) {
      this.cssElements.intro = document.createElement('div')
      this.cssElements.intro.innerHTML = `<h1 class="popup intro">${text.intro.text}</h1>`
      // if (text.intro.width) {
      // }
      let targetObject = this.intros[textIndex]
      targetObject.geometry.computeBoundingBox()
      let bbox = targetObject.geometry.boundingBox
      let width = utils.unitsToPixels(bbox.max.x - bbox.min.x)
      $(this.cssElements.intro).find('.popup.intro').css('maxWidth', width)
      this.text3DObjects.intro = new THREE.CSS3DObject(this.cssElements.intro)
      let introPosition = utils.getCenterPoint(targetObject)
      this.text3DObjects.intro.position.set(
        introPosition.x,
        introPosition.y,
        introPosition.z
      )
      this.text3DObjects.intro.scale.set(0.03, 0.03, 0.03)
      cssScene.add(this.text3DObjects.intro)
    }
    if (text.pointers) {
      this.text3DObjects.pointers = []
      text.pointers.forEach((pointer, i) => {
        let pointerIndex = i + 1
        if (this.pointers[textIndex + '.' + pointerIndex]) {
          let cssElement = $(
            `<div class="popup pointer">
            <span class="pointer-title">${pointer.title}</span>
            <span class="pointer-subtitle">${pointer.subtitle}</span>
            </div>`
          )[0]
          this.cssElements.pointers.push(cssElement)

          let pointer3DObject = new THREE.CSS3DObject(cssElement)
          let pointerPosition = utils.getCenterPoint(this.pointers[textIndex + '.' + pointerIndex])

          let targetObject = this.pointers[textIndex + '.' + pointerIndex]
          targetObject.geometry.computeBoundingBox()
          let bbox = targetObject.geometry.boundingBox
          let width = utils.unitsToPixels(bbox.max.x - bbox.min.x, 'pointer')
          $(cssElement).css('maxWidth', width)

          if (targetObject.fontSize) {
            $(cssElement).css('fontSize', targetObject.fontSize)
          }

          pointer3DObject.scale.set(0.7, 0.7, 0.7)
          pointer3DObject.position.set(
            pointerPosition.x,
            pointerPosition.y,
            pointerPosition.z
          )
          cssScene.add(pointer3DObject)
          this.text3DObjects.pointers.push(pointer3DObject)
        }
      })
    }
  }
}

export default SceneController



// WEBPACK FOOTER //
// ./src/world/SceneController.js